import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Handshake, Target, Heart, Layers, Users, Compass, Rocket, Award } from 'lucide-react'
import { trackEvent } from '../analytics'
import { Team } from '../sections/Team'

const values = [
  { icon: Handshake, title: 'Win-Win Mindset', text: 'We succeed when our stakeholders succeed. Every partnership is built on shared growth.' },
  { icon: Target, title: 'Impact', text: 'We focus on work that creates real business outcomes, not vanity metrics.' },
  { icon: Heart, title: 'Respect', text: 'We respect every voice, perspective, and decision — internally and externally.' },
  { icon: Layers, title: 'Simplicity', text: 'Clear thinking and simple systems create the strongest results.' },
  { icon: Users, title: 'People Over Pressure', text: 'Healthy teams build better systems. We prioritize people and sustainable work environments.' },
]

export default function AboutPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="min-h-screen"
    >
      {/* Hero */}
      <section className="relative pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 dark:from-primary/10 dark:to-transparent" />
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-primary/10 dark:bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-accent/10 dark:bg-primary/10 rounded-full blur-3xl" />
        
        {/* Abstract CSS Blob */}
        <div className="absolute top-20 right-10 w-64 h-64 opacity-30 dark:opacity-20 pointer-events-none select-none">
          <div className="w-full h-full bg-gradient-to-br from-primary to-accent rounded-[30%_70%_70%_30%/30%_30%_70%_70%] animate-[spin_20s_linear_infinite] blur-xl mix-blend-multiply dark:mix-blend-screen" />
        </div>
        <div className="container-narrow relative">
          <motion.span
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-accent font-semibold text-sm uppercase tracking-wider"
          >
            About us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 }}
            className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-primary dark:text-white mt-2 mb-6 max-w-3xl"
          >
            The Thinking Behind DigiSutra
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed"
          >
            Digital marketing today is often filled with noise, trends, and short-term tactics. At DigiSutra, we believe businesses deserve clarity, systems, and sustainable growth strategies.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.16 }}
            className="mt-4 text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed"
          >
            Our role is not just to run campaigns. Our role is to help businesses build predictable marketing systems that generate opportunities and support long-term growth.
          </motion.p>
        </div>
      </section>

      {/* Vision & Mission - side by side glass */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="container-wide grid md:grid-cols-2 gap-6 sm:gap-8">
          <motion.div
            className="card-glass p-6 sm:p-8"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center mb-5">
              <Compass className="text-primary dark:text-accent" size={24} strokeWidth={1.8} />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-primary dark:text-accent mb-3">Vision</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We envision leading India's digital transformation for high-value industries by building the most trusted agency for scalable growth — rooted in data, systems, and human connection.
            </p>
          </motion.div>
          <motion.div
            className="card-glass p-6 sm:p-8"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-accent/10 flex items-center justify-center mb-5">
              <Rocket className="text-primary dark:text-accent" size={24} strokeWidth={1.8} />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-primary dark:text-accent mb-3">Mission</h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              Our mission is to drive growth for high-value businesses by combining sharp strategy with systems that generate and convert leads — predictably and fast.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="container-wide">
          <motion.div
            className="flex items-center gap-3 mb-10"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Award className="text-accent" size={28} />
            <h2 className="text-2xl sm:text-3xl font-bold text-primary dark:text-white">Core Values</h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                className="card-glass p-6 sm:p-8 hover:shadow-card-hover transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <v.icon className="text-primary dark:text-accent mb-4" size={28} strokeWidth={1.8} />
                <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-lg">{v.title}</h3>
                <p className="mt-2 text-slate-600 dark:text-slate-400 text-sm sm:text-base">{v.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <Team variant="full" />

      {/* CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container-narrow text-center">
          <motion.p
            className="text-slate-600 dark:text-slate-400 mb-6"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            Ready to build your growth system?
          </motion.p>
          <Link to="/contact" className="btn-primary" onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'about_page' })}>
            Book Strategy Call
          </Link>
        </div>
      </section>
    </motion.div>
  )
}
